import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Button,
    Card,
    Col,
    Radio,
    Row,
    Select,
    Space,
    Table,
    Tag,
    Typography,
    message
} from 'antd';
import { ReloadOutlined, SaveOutlined } from '@ant-design/icons';
import axios from 'axios';
import Layout from './Layout';
import { ACCESS_LEVELS } from '../utils/pageAccess';

const { Title, Text, Paragraph } = Typography;

const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`
});

const PrivilegesPage = () => {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [mode, setMode] = useState('role');
    const [roles, setRoles] = useState([]);
    const [users, setUsers] = useState([]);
    const [roleLabels, setRoleLabels] = useState({});
    const [subjectId, setSubjectId] = useState(1);
    const [matrix, setMatrix] = useState([]);
    const [draft, setDraft] = useState({});
    const [counterparties, setCounterparties] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [portalNote, setPortalNote] = useState(null);

    const loadSubjects = useCallback(async () => {
        const { data } = await axios.get(
            '/api/page-access/subjects',
            { headers: authHeaders() }
        );
        setRoles(data.data.roles || []);
        setUsers(data.data.users || []);
        setRoleLabels(data.data.role_labels || {});
    }, []);

    const loadMatrix = useCallback(async () => {
        if (!subjectId) return;
        setLoading(true);
        try {
            const { data } = await axios.get(
                '/api/page-access/matrix',
                {
                    headers: authHeaders(),
                    params: {
                        subject_type: mode,
                        subject_id: subjectId
                    }
                }
            );
            const rows = data.data.matrix || [];
            setMatrix(rows);
            const next = {};
            rows.forEach(row => {
                next[row.key] = row.access_level;
            });
            setDraft(next);
            setCounterparties(data.data.counterparties || []);
            setSelectedUser(data.data.user || null);
            setPortalNote(data.data.portal_note || null);
        } catch (error) {
            message.error(
                error?.response?.data?.message ||
                    'Ошибка загрузки матрицы'
            );
        } finally {
            setLoading(false);
        }
    }, [mode, subjectId]);

    useEffect(() => {
        loadSubjects().catch(error =>
            message.error(
                error?.response?.data?.message ||
                    'Ошибка загрузки'
            )
        );
    }, [loadSubjects]);

    useEffect(() => {
        loadMatrix();
    }, [loadMatrix]);

    const isPortalSubject = useMemo(() => {
        if (mode === 'role') {
            return [4, 5].includes(Number(subjectId));
        }
        return Boolean(selectedUser?.is_portal) ||
            [4, 5].includes(Number(selectedUser?.role_id));
    }, [mode, subjectId, selectedUser]);

    const subjectOptions = useMemo(() => {
        if (mode === 'role') {
            return roles.map(role => ({
                value: role.id,
                label: `${roleLabels[role.id] || role.name} (#${role.id})`
            }));
        }
        return users.map(user => ({
            value: user.id,
            label: `${user.name || user.email} — ${
                roleLabels[user.role_id] || `роль ${user.role_id}`
            }${
                user.counterparties?.length
                    ? ` · КА: ${user.counterparties
                          .map(c => c.name)
                          .join(', ')}`
                    : ''
            }`
        }));
    }, [mode, roles, users, roleLabels]);

    const save = async () => {
        if (isPortalSubject) {
            message.warning(
                'Права порталов заказчика/монтажника нельзя менять здесь'
            );
            return;
        }

        setSaving(true);
        try {
            const items = Object.entries(draft)
                .filter(([key]) => {
                    const page = matrix.find(m => m.key === key);
                    return page && page.group !== 'portal';
                })
                .map(([page_key, access_level]) => ({
                    page_key,
                    access_level
                }));

            await axios.put(
                '/api/page-access/bulk',
                {
                    subject_type: mode,
                    subject_id: subjectId,
                    items
                },
                { headers: authHeaders() }
            );
            message.success('Права сохранены');
            loadMatrix();
        } catch (error) {
            message.error(
                error?.response?.data?.message ||
                    'Ошибка сохранения'
            );
        } finally {
            setSaving(false);
        }
    };

    const clearOverrides = async () => {
        if (mode !== 'user') return;
        try {
            await axios.delete(
                `/api/page-access/user/${subjectId}`,
                { headers: authHeaders() }
            );
            message.success(
                'Персональные переопределения сброшены'
            );
            loadMatrix();
        } catch (error) {
            message.error(
                error?.response?.data?.message ||
                    'Ошибка сброса'
            );
        }
    };

    const columns = [
        {
            title: 'Страница',
            dataIndex: 'title',
            render: (title, row) => (
                <Space direction="vertical" size={0}>
                    <Text strong>{title}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        {row.path}
                    </Text>
                </Space>
            )
        },
        {
            title: 'Группа',
            dataIndex: 'group',
            width: 110,
            render: group => {
                const map = {
                    staff: 'CRM',
                    admin: 'Админ',
                    hr: 'HR',
                    portal: 'Портал'
                };
                return <Tag>{map[group] || group}</Tag>;
            }
        },
        ...(mode === 'user'
            ? [
                  {
                      title: 'По роли',
                      dataIndex: 'role_access_level',
                      width: 140,
                      render: level =>
                          ACCESS_LEVELS.find(
                              item => item.value === level
                          )?.label || level
                  }
              ]
            : []),
        {
            title: 'Доступ',
            width: 220,
            render: (_, row) => (
                <Select
                    style={{ width: '100%' }}
                    value={draft[row.key] || 'none'}
                    disabled={row.locked || isPortalSubject}
                    options={ACCESS_LEVELS}
                    onChange={value =>
                        setDraft(prev => ({
                            ...prev,
                            [row.key]: value
                        }))
                    }
                />
            )
        },
        {
            title: '',
            width: 120,
            render: (_, row) =>
                row.has_user_override ? (
                    <Tag color="purple">override</Tag>
                ) : row.group === 'portal' ? (
                    <Tag color="gold">системный</Tag>
                ) : null
        }
    ];

    return (
        <Layout>
            <div style={{ padding: 16 }}>
                <Space
                    style={{
                        width: '100%',
                        justifyContent: 'space-between',
                        marginBottom: 16
                    }}
                    wrap
                >
                    <div>
                        <Title level={3} style={{ margin: 0 }}>
                            Права доступа к страницам
                        </Title>
                        <Text type="secondary">
                            Настройка: кому какая страница доступна
                            и с каким уровнем прав
                        </Text>
                    </div>
                    <Space wrap>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={loadMatrix}
                        >
                            Обновить
                        </Button>
                        {mode === 'user' ? (
                            <Button onClick={clearOverrides}>
                                Сбросить overrides
                            </Button>
                        ) : null}
                        <Button
                            type="primary"
                            icon={<SaveOutlined />}
                            loading={saving}
                            disabled={isPortalSubject}
                            onClick={save}
                        >
                            Сохранить
                        </Button>
                    </Space>
                </Space>

                <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="Заказчик и монтажник — отдельные порталы"
                    description="Их доступ к данным идёт через привязку к контрагентам (counterparty_ids). Страницы кабинета заказчика и монтажника/поставщика матрицей не открываются другим ролям и не отбираются у 4/5 — текущая логика заказов не ломается."
                />

                <Card size="small" style={{ marginBottom: 16 }}>
                    <Row gutter={16} align="middle">
                        <Col xs={24} md={8}>
                            <Radio.Group
                                value={mode}
                                onChange={e => {
                                    const next = e.target.value;
                                    setMode(next);
                                    setSubjectId(
                                        next === 'role'
                                            ? 1
                                            : users[0]?.id || null
                                    );
                                }}
                                options={[
                                    {
                                        value: 'role',
                                        label: 'По ролям'
                                    },
                                    {
                                        value: 'user',
                                        label: 'По пользователям'
                                    }
                                ]}
                            />
                        </Col>
                        <Col xs={24} md={16}>
                            <Select
                                style={{ width: '100%' }}
                                showSearch
                                optionFilterProp="label"
                                value={subjectId || undefined}
                                options={subjectOptions}
                                onChange={setSubjectId}
                                placeholder={
                                    mode === 'role'
                                        ? 'Выберите роль'
                                        : 'Выберите пользователя'
                                }
                            />
                        </Col>
                    </Row>
                </Card>

                {portalNote ? (
                    <Alert
                        type="warning"
                        showIcon
                        style={{ marginBottom: 16 }}
                        message={portalNote}
                    />
                ) : null}

                {mode === 'user' && selectedUser ? (
                    <Card
                        size="small"
                        style={{ marginBottom: 16 }}
                        title="Пользователь и контрагенты"
                    >
                        <Paragraph style={{ marginBottom: 8 }}>
                            <Text strong>
                                {selectedUser.name ||
                                    selectedUser.email}
                            </Text>
                            {' · '}
                            {roleLabels[selectedUser.role_id] ||
                                `роль ${selectedUser.role_id}`}
                        </Paragraph>
                        {counterparties.length ? (
                            <Space wrap>
                                {counterparties.map(item => (
                                    <Tag key={item.id} color="cyan">
                                        {item.name}
                                    </Tag>
                                ))}
                            </Space>
                        ) : (
                            <Text type="secondary">
                                Контрагенты не привязаны
                                {[4, 5].includes(
                                    Number(selectedUser.role_id)
                                )
                                    ? ' — для портала заказчика/монтажника задачи не отфильтруются'
                                    : ''}
                            </Text>
                        )}
                    </Card>
                ) : null}

                <Table
                    rowKey="key"
                    loading={loading}
                    dataSource={matrix}
                    columns={columns}
                    pagination={false}
                    size="middle"
                />
            </div>
        </Layout>
    );
};

export default PrivilegesPage;
